(ns renolit-roofing-service.view.installer-review-wp-detail
  (:require [clojure.string :as str]
            [malli.util :as mu]
            [re-frame.core :as rf]
            [renolit-roofing-service.layout.components :refer [collapsable-section]]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.navigation :as nav]
            [renolit-roofing-service.service.service :as srv.service]
            [renolit-roofing-service.shared.domain.types :as dom.types]
            [renolit-roofing-service.view :as view]
            [renolit-roofing-service.view.installer-add-observation-popup :as add-observation-popup]
            [renolit-roofing-service.view.installer-service-detail :as view.service-detail]))

(def path-params-schema
  [:map
   [:id [:uuid]]
   [:wp [:enum :visual-inspection :destructive-testing :non-destructive-testing]]])

(def ^:const route-config
  {:name [:installer-review-wp-detail]
   :auth-req? true
   :parameters {:path path-params-schema}
   :controllers [{:parameters {:path (mu/keys path-params-schema)}
                  :start #(rf/dispatch [::view.on-enter %])
                  :stop #(rf/dispatch [::view.on-leave %])}]})

(rf/reg-event-fx
 ::view.on-enter
 (fn [_ [_ opts]]
   (let [service-id (get-in opts [:path :id])
         wp (get-in opts [:path :wp])]
     {:fx [[:dispatch [::set-wp wp]]
           [:dispatch [::srv.service/fetch-service-by-id service-id]]]})))

(rf/reg-event-fx
 ::view.on-leave
 (fn [_ _]
   {:fx [[:dispatch [::clear-wp]]]}))

(def ^:private wp-type-defaults
  "First wp-type tab to show for each wp."
  {:visual-inspection :from-below
   :non-destructive-testing :nil
   :destructive-testing :nil})

(rf/reg-event-db
 ::set-wp
 (fn [db [_ wp]]
   (-> db
       (assoc :vi-wp wp)
       (assoc :vi-active-tab (get wp-type-defaults wp :from-below)))))

(rf/reg-sub
 ::current-wp
 (fn [db _]
   (get db :vi-wp :visual-inspection)))

(rf/reg-event-db
 ::set-active-tab
 (fn [db [_ tab]]
   (assoc db :vi-active-tab tab)))

(rf/reg-sub
 ::active-tab
 (fn [db _]
   (get db :vi-active-tab :from-below)))

(rf/reg-event-fx
 ::toggle-checked
 (fn [_ [_ {:keys [service-id review-id task-type wp clear-obs]}]]
   (if clear-obs
     {:fx [[:dispatch [::srv.service/delete-service-observation
                       service-id (:id clear-obs)]]]}
     {:fx [[:dispatch [::srv.service/append-service-observation
                       service-id
                       {:id (random-uuid)
                        :ui-id (str "clear-" (random-uuid))
                        :review-id review-id
                        :wp wp
                        :task-type task-type
                        :clear true}]]]})))

(rf/reg-event-db
 ::clear-wp
 (fn [db _]
   (dissoc db :vi-wp)))

(rf/reg-sub
 ::clear-observations-index
 :<- [::srv.service/current-service]
 (fn [service _]
   (let [observations (or (:observations service) [])]
     (reduce (fn [acc obs]
               (let [k [(str (:review-id obs)) (keyword (:task-type obs))]]
                 (assoc acc k obs)))
             {}
             (filter :clear observations)))))

(rf/reg-sub
 ::observations-by-review-task
 :<- [::srv.service/current-service]
 (fn [service _]
   (let [observations (or (:observations service) [])]
     (reduce (fn [acc obs]
               (let [k [(str (:review-id obs)) (keyword (:task-type obs))]]
                 (update acc k (fnil conj []) obs)))
             {}
             observations))))

(rf/reg-sub
 ::zone-name-index
 :<- [::srv.service/current-service]
 (fn [service _]
   (let [zones (:zones service)]
     (reduce (fn [acc z]
               (assoc acc (:id z) (:name z)))
             {}
             zones))))

(rf/reg-sub
 ::wp-reviews
 :<- [::srv.service/current-service]
 :<- [::zone-name-index]
 :<- [::current-wp]
 (fn [[service zone-names wp] _]
   (let [reviews (:reviews service)]
     (->> reviews
          (filter #(= (name wp) (name (:wp %))))
          (map #(assoc % :zone-name (get zone-names (:zone-id %))))))))

(rf/reg-sub
 ::reviews-by-wp-type
 :<- [::wp-reviews]
 (fn [reviews _]
   (group-by :wp-type reviews)))

(rf/reg-sub
 ::wp-type-counts
 :<- [::reviews-by-wp-type]
 (fn [grouped _]
   (reduce-kv (fn [acc wp-type revs]
                (assoc acc wp-type (count (mapcat :tasks revs))))
              {}
              grouped)))

(rf/reg-sub
 ::current-tab-reviews
 :<- [::reviews-by-wp-type]
 :<- [::active-tab]
 (fn [[grouped active-tab] _]
   (get grouped (name active-tab) [])))

(rf/reg-sub
 ::current-tab-data
 :<- [::current-tab-reviews]
 (fn [reviews _]
   ;; Group by task-type, then by revision type (:general/:focused)
   ;; Returns: {task-type {:general [rows...] :focused [rows...]}}
   (let [rows (mapcat (fn [review]
                        (map (fn [task]
                               {:review-id (:id review)
                                :type (keyword (:type review))
                                :zone-id (:zone-id review)
                                :zone-name (:zone-name review)
                                :task-type (keyword (:task-type task))
                                :comment (:comment task)})
                             (:tasks review)))
                      reviews)]
     (reduce (fn [acc row]
               (update-in acc [(:task-type row) (:type row)]
                          (fnil conj [])
                          row))
             {}
             rows))))

(rf/reg-sub
 ::all-item-keys
 :<- [::current-tab-data]
 (fn [tab-data _]
   (into #{}
         (for [[task-type type-map] tab-data
               [rev-type rows] type-map
               row rows]
           [(:review-id row) (:task-type row)]))))

(rf/reg-sub
 ::all-checked?
 :<- [::all-item-keys]
 :<- [::clear-observations-index]
 (fn [[all-keys clear-index] _]
   (and (seq all-keys)
        (every? #(contains? clear-index %) all-keys))))

(defn- wp-type-tabs
  []
  (let [active-tab (rf/subscribe [::active-tab])
        counts (rf/subscribe [::wp-type-counts])
        wp (rf/subscribe [::current-wp])]
    (fn []
      (let [wp-types (keys (get dom.types/wp-hierarchy @wp))
            tabs (mapv (fn [wpt]
                         {:key wpt
                          :label (t [(keyword (str "domain.choices.wp-type/" (name wpt)))])})
                       wp-types)]
        (when (> (count tabs) 1)
          [:div.vi-tabs.tab-bar.tab-bar--pill
           (doall
            (for [{:keys [key label]} tabs]
              ^{:key key}
              [:div.vi-tab.tab-bar__tab
               {:class (when (= key @active-tab) "tab-bar__tab--selected")
                :data-key key
                :on-click #(rf/dispatch [::set-active-tab key])}
               (str label " (" (get @counts (name key) 0) ")")]))])))))

(defn- filter-button
  []
  [:div.vi-filter-bar
   [:button.btn.btn--transparent.btn--anchor.btn--small
    {:on-click #(js/console.log "Filters clicked")}
    [layout.icon/icon {:key :filter :class "vi-filter-icon"}]
    (t [:page.installer.review-wp-detail.filters])]])

(defn- observation-card
  [obs]
  (let [level (:incidence-level obs)
        label (or (:ui-id obs) (str "I" (subs (str (:id obs)) 0 (min 8 (count (str (:id obs)))))))]
    [:div.vi-observation-card
     {:class (when level (str "vi-observation-card--" (name level)))}
     [layout.icon/icon {:key :warning :class "vi-observation-card__icon"}]
     [:span.vi-observation-card__label label]]))

(defn- review-item
  [row wp active-tab]
  (let [service @(rf/subscribe [::srv.service/current-service])
        obs-index @(rf/subscribe [::observations-by-review-task])
        clear-index @(rf/subscribe [::clear-observations-index])
        item-key [(:review-id row) (:task-type row)]
        observations (get obs-index item-key [])
        clear-obs (get clear-index item-key)
        checked? (some? clear-obs)
        action-label (case (:type row)
                       :focused (t [:page.installer.review-wp-detail.view-specific-point])
                       :general (t [:page.installer.review-wp-detail.view-zone])
                       nil)]
    [:div.vi-review-item
     [:div.vi-review-item__row
      [:div.vi-review-item__checkbox
       {:class (when checked? "vi-review-item__checkbox--checked")
        :on-click #(rf/dispatch [::toggle-checked
                                 {:service-id (:id service)
                                  :review-id (:review-id row)
                                  :task-type (:task-type row)
                                  :wp wp
                                  :clear-obs clear-obs}])}
       (when checked?
         [:span.vi-review-item__checkmark])]
      [:div.vi-review-item__content
       [:div.vi-review-item__zone-row
        (when (:zone-name row)
          [:span.vi-review-item__zone-pill (:zone-name row)])
        (when action-label
          [:span.vi-review-item__zone-label action-label])]
       (when (and (:comment row) (not (str/blank? (:comment row))))
         [:div.vi-review-item__comment
          [layout.icon/icon {:key :message :class "vi-review-item__comment-icon"}]
          [:span.vi-review-item__comment-text (:comment row)]])
       (let [visible-obs (remove :clear observations)]
         (when (seq visible-obs)
           [:div.vi-observation-list
            (for [obs visible-obs]
              ^{:key (str (:id obs))}
              [observation-card obs])]))]]
     (when-not checked?
       [:button.vi-review-item__action
        {:on-click #(rf/dispatch [::add-observation-popup/open
                                  {:wp wp
                                   :wp-type active-tab
                                   :review-id (:review-id row)
                                   :task-type (:task-type row)
                                   :zone-id (:zone-id row)
                                   :service-id (:id service)}])}
        (t [:page.installer.review-wp-detail.add-observation])])]))

(defn- revision-type-section
  [rev-type rows wp active-tab]
  (let [label (t [(keyword (str "domain.choices.revision-type/"
                                (case rev-type
                                  :focused "focused-revision"
                                  :general "general-revision"
                                  "general-revision")))])]
    [:div.vi-revision-type-section
     [:span.vi-revision-type-section__title label]
     (for [row rows]
       ^{:key (str (:review-id row) "-" (name (:task-type row)) "-" (:zone-name row))}
       [review-item row wp active-tab])]))

(defn- task-type-card
  [task-type type-map wp active-tab]
  (let [title (t [(keyword (str "domain.choices.task-type/" (name task-type)))])]
    [:<>
     [collapsable-section
      {:title title
       :class "vi-task-type-card"
       :start-open true}
      [:div.vi-task-type-content
       (when-let [focused (:focused type-map)]
         [revision-type-section :focused focused wp active-tab])
       (when-let [general (:general type-map)]
         [revision-type-section :general general wp active-tab])]]]))

(defn- fab-button
  [service-id]
  [:button.fab-button
   {:on-click #(rf/dispatch [::add-observation-popup/open {:service-id service-id}])}
   [:span.fab-button__icon "+"]])

(defn- bottom-action-button
  [service-id]
  (let [all-completed? (rf/subscribe [::view.service-detail/all-work-packages-completed?])]
    (fn [service-id]
      [:div.bottom-action
       [:button.btn.btn--primary.btn--full-width
        {:disabled (not @all-completed?)
         :on-click #(rf/dispatch [::view.service-detail/open-confirm-submit {:service-id service-id}])}
        (t [:page.installer.review-wp-detail.finalize-and-send])]])))

(defn- main
  []
  (let [service (rf/subscribe [::srv.service/current-service])
        tab-data (rf/subscribe [::current-tab-data])
        wp (rf/subscribe [::current-wp])
        active-tab (rf/subscribe [::active-tab])
        submitted? (rf/subscribe [::view.service-detail/service-submitted?])]
    (fn []
      (let [service-id (:id @service)
            wp-label (t [(keyword (str "domain.choices.wp/" (name @wp)))])]
        [:div.user_layout
         [:div.user_layout_header
          [:a.btn.btn--transparent.btn--anchor.back-link
           {:href (nav/href [:installer-service-detail] {:id service-id})}
           [layout.icon/icon {:key :back}]]
          [:h3 wp-label]
          [:div]]
         [:div.installer-review-wp-detail
          (if @service
            [:<>
             [wp-type-tabs]
             #_[filter-button]
             [:div.vi-content
              (if (seq @tab-data)
                (for [[task-type type-map] (sort-by (fn [[k _]] (name k)) @tab-data)]
                  ^{:key (name task-type)}
                  [task-type-card task-type type-map @wp @active-tab])
                [:div.empty-state
                 [:p (t [:page.installer.review-wp-detail.no-reviews])]])]
             (when-not @submitted?
               [:<>
                #_[fab-button service-id]
                [bottom-action-button service-id]])]
            [:div.loading (t [:common.loading])])]]))))

(defmethod view/view-display (:name route-config)
  [_]
  [main])
